import React, { useState, useEffect, useCallback } from 'react';
import { GameUIProps } from '../../types/game.types';
import { useGameManager } from '../../hooks/useGameManager';
import { useAppContext } from '../../hooks/useAppContext';
import { useChat } from '../../hooks/useChat';
import { useEditMode } from '../../hooks/useEditMode';
import { useVideoView } from '../../hooks/useVideoView.js';
import { useGameScene } from '../../contexts/GameInstanceContext';
import UserInfoBar from './UserInfoBar';
import AdminPanel from './AdminPanel';
import Notifications from './Notifications';
import { PlayerDetailsModal, UserSettingsModal, HomeZoneModal, SessionReplacedModal } from '../modals';
import { ChatDrawer } from '../chat/ChatDrawer';
import { VideoWrapper } from '../video/VideoWrapper.js';
import { DecorationDrawer } from '../decorations/DecorationDrawer';

/** Why "Customize Island" is unavailable while the call is full screen (#123). */
const CUSTOMIZE_DISABLED_IN_FULL_SCREEN = 'Exit full-screen video to customize';

const GameUI: React.FC<GameUIProps> = ({ className, roomConnection }) => {
  const { adminPanel, closeAdminPanel, playerDetailsModal, closePlayerDetails, userSettingsModal, closeUserSettingsModal, homeZoneModal, closeZoneModal } = useGameManager();
  const { auth, room } = useAppContext();
  const gameScene = useGameScene();
  const [isDecorationDrawerOpen, setIsDecorationDrawerOpen] = useState(false);
  // The island's one chat: the drawer's open state and the unread badge
  const { isOpen: isChatOpen, toggle: toggleChat, totalUnread } = useChat();
  const editMode = useEditMode();
  const [mergedZones, setMergedZones] = useState<any[]>([]);
  // Full-screen video (#123): it locks out decorating and sits beside the chat
  const { isFullScreen } = useVideoView();
  const [chatDrawerWidth, setChatDrawerWidth] = useState(0);

  // The private area the player stands in names the call in full screen
  const callLabel = roomConnection.privateAreas?.find((area) =>
    area.occupants?.some((occupant) => occupant.userId === auth.user?.id)
  )?.name;

  // Merge tilemap zones with backend zones whenever either updates
  useEffect(() => {
    if (!gameScene) return;

    // Get zones from tilemap in game scene
    const tilemapZones = gameScene.getAllZones?.() || [];
    const backendZones = roomConnection.homeZones || [];

    // Create a map for easy lookup
    const mergedZonesMap = new Map<string, any>();

    // Start with tilemap zones (have geometry)
    tilemapZones.forEach(zone => {
      mergedZonesMap.set(zone.id?.toString(), { ...zone });
    });

    // Merge in backend zone claims
    backendZones.forEach(backendZone => {
      const zoneId = (backendZone.id || backendZone.zoneId)?.toString();
      if (zoneId) {
        const existing = mergedZonesMap.get(zoneId);
        if (existing) {
          // Merge backend claims with tilemap geometry
          mergedZonesMap.set(zoneId, {
            ...existing,
            claimedBy: backendZone.claimedBy,
            claimedByUsername: backendZone.claimedByUsername,
            claimedByAvatar: backendZone.claimedByAvatar,
            claimedByStatus: backendZone.claimedByStatus,
            claimedByIsConnected: backendZone.claimedByIsConnected,
            claimedAt: backendZone.claimedAt
          });
        } else {
          // Backend zone without tilemap match (shouldn't happen)
          mergedZonesMap.set(zoneId, backendZone);
        }
      }
    });

    const merged = Array.from(mergedZonesMap.values());
    setMergedZones(merged);
  }, [gameScene, roomConnection.homeZones]);

  // Keyboard shortcut for chat (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        toggleChat();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleChat]);
  
  // Update Phaser input blocking when chat drawer state changes
  useEffect(() => {
    try {
      if (gameScene?.setChatDrawerState) {
        // Set chat state with drawer width (600px on desktop, full width on mobile)
        gameScene.setChatDrawerState(isChatOpen, 600);
      }
    } catch (error) {
      console.warn('Failed to set chat drawer state:', error);
    }
  }, [isChatOpen, gameScene]);
  
  /**
   * Handle chat drawer width changes to adjust camera bounds
   * This allows the game world to remain draggable when the drawer is open,
   * and full-screen video to start to the right of it
   * @param width - The current width of the chat drawer (0 when closed)
   */
  const handleChatDrawerWidthChange = useCallback((width: number) => {
    setChatDrawerWidth(width);
    try {
      if (gameScene?.adjustCameraForDrawer) {
        // Adjust camera bounds to accommodate drawer
        gameScene.adjustCameraForDrawer(width);
      }
    } catch (error) {
      console.warn('Failed to adjust camera for chat drawer:', error);
    }
  }, [gameScene]);

  const handlePlayerRespawn = async (userId: string): Promise<void> => {
    // Get spawn point from GameScene
    const spawnPoint = gameScene?.getSpawnPoint?.();

    if (spawnPoint) {
      await roomConnection.teleportPlayer(userId, spawnPoint);
    } else {
      console.error('Could not get spawn point from GameScene');
    }
  };

  // A session taken over by another device or tab (#132) given up for good:
  // back to the island list, the way the exit button leaves.
  const handleLeaveReplacedSession = async () => {
    try {
      await roomConnection.leaveRoom();
    } catch (error) {
      console.error('Error leaving a replaced session:', error);
    } finally {
      room.clearRoomState();
    }
  };

  // Handle decoration drawer toggle
  const handleCustomizeIslandToggle = () => {
    // Decorating needs the world on screen (epic #122 D4)
    if (isFullScreen) return;

    if (editMode.isActive) {
      // If already in edit mode, close the drawer and exit edit mode
      editMode.exitEditMode();
      setIsDecorationDrawerOpen(false);
    } else {
      // Enter edit mode and open the drawer
      editMode.enterEditMode();
      setIsDecorationDrawerOpen(true);
    }
  };

  // Handle decoration drawer close
  const handleDecorationDrawerClose = () => {
    setIsDecorationDrawerOpen(false);
    if (editMode.isActive) {
      editMode.exitEditMode();
    }
  };

  // Entering full-screen video leaves edit mode and closes its drawer (D4)
  const { isActive: isEditing, exitEditMode } = editMode;
  useEffect(() => {
    if (!isFullScreen) return;
    setIsDecorationDrawerOpen(false);
    if (isEditing) {
      exitEditMode();
    }
  }, [isFullScreen, isEditing, exitEditMode]);

  // Sync decoration drawer state with edit mode
  useEffect(() => {
    if (!editMode.isActive && isDecorationDrawerOpen) {
      setIsDecorationDrawerOpen(false);
    }
  }, [editMode.isActive, isDecorationDrawerOpen]);

  return (
    <div className={`pointer-events-none z-[1000] ${className || ''}`}>
      {/* User Info Bar - Bottom of screen */}
      <UserInfoBar
        roomConnection={roomConnection}
        onChatToggle={toggleChat}
        onCustomizeIslandToggle={handleCustomizeIslandToggle}
        customizeIslandDisabledHint={isFullScreen ? CUSTOMIZE_DISABLED_IN_FULL_SCREEN : undefined}
        totalUnreadCount={totalUnread}
      />

      {/* Admin Panel Modal */}
      <AdminPanel
        isOpen={adminPanel.isOpen}
        onClose={closeAdminPanel}
      />

      {/* Player Details Modal */}
      <PlayerDetailsModal
        isOpen={playerDetailsModal.isOpen}
        playerData={playerDetailsModal.playerData}
        onClose={closePlayerDetails}
        onRespawn={handlePlayerRespawn}
      />

      {/* User Settings Modal */}
      {auth.user && (
        <UserSettingsModal
          isOpen={userSettingsModal.isOpen}
          onClose={closeUserSettingsModal}
          user={auth.user}
          onUpdateProfile={auth.updateProfile}
          isLoading={auth.isLoading}
        />
      )}

      {/* Home Zone Modal */}
      {auth.user && (
        <HomeZoneModal
          isOpen={homeZoneModal.isOpen}
          zoneId={homeZoneModal.zoneId}
          currentUserId={auth.user.id}
          allZones={mergedZones}
          onClose={closeZoneModal}
          onClaimZone={roomConnection.claimZone}
          onUnclaimZone={roomConnection.unclaimZone}
        />
      )}

      {/* Notifications - Lower right corner, above the bottom bar (DESIGN_SYSTEM.md, "Toasts") */}
      <Notifications
        className=""
        position="bottom-right"
        roomConnection={roomConnection}
      />

      {/* Video heads-up display - one row at the top, or full screen beside
          the chat, behind its own error boundary so a crash in a tile cannot
          take the rest of the HUD down with it */}
      <VideoWrapper callLabel={callLabel} chatDrawerWidth={chatDrawerWidth} />


      {/* Chat Drawer */}
      <ChatDrawer onWidthChange={handleChatDrawerWidthChange} />

      {/* Decoration Drawer */}
      <DecorationDrawer
        isOpen={isDecorationDrawerOpen}
        onClose={handleDecorationDrawerClose}
        editMode={editMode}
      />

      {/* Session taken over by another device or tab - last, so it covers
          every other overlay on the island */}
      <SessionReplacedModal
        isOpen={roomConnection.sessionReplaced}
        onReclaim={roomConnection.reclaimSession}
        onLeave={() => void handleLeaveReplacedSession()}
      />
    </div>
  );
};

export default GameUI;